import React, { useState, useEffect } from 'react';
import useAuth from '../../hooks/useAuth';
import Select from 'react-select';
import DashboardTab from './tabs/DashboardTab';
import TopCustomerSalesTrendTab from './tabs/TopCustomerSalesTrendTab';
import CustomerRankingTab from './tabs/CustomerRankingTab';
import SalesTrendingReportService from '../../services/SalesTrendingReportService';
import moment from 'moment';
import './sales.css';

const SalesTrendingReportPage = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('dashboard');
    
    // Data States
    const [loading, setLoading] = useState(true);
    const [topCustomers, setTopCustomers] = useState([]);
    const [totalSalesData, setTotalSalesData] = useState({});
    const [categories, setCategories] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [agencies, setAgencies] = useState([]);
    const [selectedAgency, setSelectedAgency] = useState(null);
    const [showDataFor, setShowDataFor] = useState(null);    
    const salesTrendingReportService = new SalesTrendingReportService();

    useEffect(() => {
        if(user) {
            fetchAgencis();
            setShowDataFor(user.name);
        }
        const localCustomers = []
        for (let i = 0; i < 15; i++) {
            localCustomers.push({
                name: '',
                is_loader: true,
                data: []
            });
        }
        setCustomers(localCustomers);
    }, [user]);

    useEffect(() => {
        if(showDataFor) {
            fetchTopCustomers();
            fetchTotalSales();
        }
    }, [showDataFor]);

    const fetchAgencis = () => {
        if (user.username !== 'TTCS') return;
        const payload = {
            portal_user_name: user.name,
            user_type: "associate"
        };

        salesTrendingReportService.getAgencies(payload)
            .then((response) => {
                if (response.status === 200 && response.data.status === 'success') {
                    const agenciesSet = response.data.result.map(c => ({
                        value: c.name,
                        label: c.name
                    }));
                    setAgencies(agenciesSet);
                }
            });
    };

    const fetchTopCustomers = () => {
        var payload = {
            "portal_user_name": showDataFor,
            "start_date": moment().subtract(12, 'months').format('YYYY-MM-DD'),
            "end_date": moment().format('YYYY-MM-DD'),
            "limit": 15,
            "order_by": "desc"
        };

        salesTrendingReportService.getCustomerBySales(payload).then((response) => {
            setLoading(false);
            if (response.status === 200) {
                if (response.data.status === 'success') {
                    // 1. Process Categories
                    const categoriesSet = new Set();
                    response.data.result.forEach(customer => {
                        if (customer.category) {
                            categoriesSet.add(customer.category);
                        }
                    });
                    
                    // Convert to initial object structure
                    const categoriesList = Array.from(categoriesSet).map(categoryName => ({
                        name: categoryName,
                        is_loader: true,
                        data: []
                    }));

                    const customersSet = new Set();
                    response.data.result.forEach(customer => {
                        customersSet.add({
                            name: customer.customer_name,
                            is_loader: true,
                            data: []
                        });
                    });
                    const customersList = Array.from(customersSet);

                    // 2. Update States
                    setCustomers(customersList);

                    setCategories(categoriesList);
                    setTopCustomers(response.data.result);
                } else {
                    console.error('Error:', response.data.message);
                }
            } else {
                console.error('HTTP Error:', response.status);
            }
        });
    }

    const fetchTotalSales = () => {
        var payload = {
            "portal_user_name": showDataFor,
            "current_month": moment().format('YYYY-MM')
        };

        salesTrendingReportService.getTotalSales(payload).then((response) => {
            if (response.status === 200) {
                if (response.data.status === 'success') {
                    setTotalSalesData(response.data.result);
                } else {
                    console.error('Error:', response.data.message);
                }
            } else {
                console.error('HTTP Error:', response.status);
            }
        });
    }

    const updateAgency = (item) => {
        // CLEAR CASE
        if (!item) {
            setSelectedAgency(null);
            setShowDataFor(user.name);   // reset to logged-in user
            setTopCustomers([]);
            setCategories([]);
            setTotalSalesData({});
            setLoading(true);

            const localCustomers = Array.from({ length: 15 }, () => ({
                name: '',
                is_loader: true,
                data: []
            }));
            setCustomers(localCustomers);
            return;
        }

        // SELECT CASE
        setSelectedAgency(item);
        setShowDataFor(item.value);
        setTopCustomers([]);
        setCategories([]);
        setTotalSalesData({});
        setLoading(true);

        const localCustomers = Array.from({ length: 15 }, () => ({
            name: '',
            is_loader: true,
            data: []
        }));
        setCustomers(localCustomers);
    };

    return (
        <main className="techtop-middle-div">
            <section className="techtop-news-section pb-5">
                <div className="container">
                    <div className="row">
                        <div className="col-md-12">
                            <div className="techtop-default-title2">
                                <div className="row">
                                    <div className="col-md-6">
                                        <h1 className="m-0 dark-color1">Sales Trending Report</h1>
                                    </div>
                                    <div className="col-md-6 text-end">
                                        <h1 className="m-0 dark-color1">{user.name}</h1>
                                    </div>
                                </div>
                            </div>
                            {(user.username === 'TTCS') && (
                                <React.Fragment>
                                    <div className="row agencyDiv mt-3">
                                        <div className="col-md-4 align-self-center">
                                            <h5 className="m-0 dark-color1 text-end">Agency</h5>
                                        </div>
                                        <div className="col-md-6">
                                            <Select
                                                classNamePrefix="react-select"
                                                placeholder="Search by Agency..."
                                                options={agencies}
                                                value={selectedAgency}
                                                isClearable
                                                onChange={(selected) => updateAgency(selected)}
                                            />

                                        </div>
                                    </div>
                                </React.Fragment>
                            )}
                            <div className="row">
                                <div className="col-md-12 mt-4" id="techtop-news-tabs">
                                    {/* Tabs */}
                                    <ul className="nav nav-tabs">
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${activeTab === 'dashboard' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('dashboard')}
                                            >
                                                Dashboard
                                            </button>
                                        </li>

                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${activeTab === 'topCustomer' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('topCustomer')}
                                            >
                                                Top Customer Sales Trend
                                            </button>
                                        </li>

                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${activeTab === 'ranking' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('ranking')}
                                            >
                                                Customer Ranking
                                            </button>
                                        </li>
                                    </ul>
                                    {/* Tab Content */}
                                    <div className="tab-content mt-3">
                                        <DashboardTab 
                                            active={activeTab === 'dashboard'} 
                                            topCustomers={topCustomers}
                                            totalSalesData={totalSalesData}
                                            categories={categories}
                                            loading={loading}
                                            showDataFor={showDataFor}
                                        />
                                        <TopCustomerSalesTrendTab 
                                            active={activeTab === 'topCustomer'}
                                            customers={customers}
                                            showDataFor={showDataFor}
                                        />
                                        <CustomerRankingTab 
                                            active={activeTab === 'ranking'}
                                            showDataFor={showDataFor}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default SalesTrendingReportPage;